import { useLayoutEffect, useRef } from 'react'
import { circleAll, coordinates, coordinatesMedium } from '../shared'
import type { Radar } from 'shared'
import { usePreviousValue } from '@/hooks/use-previous-value'

export function Lines(props: { indicators: Radar['indicators']; red?: boolean; compare?: boolean }) {
  const nextLines = generateLineArray(props.indicators, props.compare)

  // The painted geometry is its own change key, because nothing else here identifies a change — docs/notes/app/radar.md.
  const { current: lines, previous: previousLines } = usePreviousValue(nextLines, Object.values(nextLines).join('|'))

  const groupRef = useRef<SVGGElement>(null)

  // LAYOUT, not passive: `beginElement()` has to land before the paint, and inside this `<g>` — docs/notes/app/radar.md.
  useLayoutEffect(() => {
    groupRef.current?.querySelectorAll<SVGAnimateElement>('animate').forEach((el) => el.beginElement())
  }, [lines])

  return (
    <g ref={groupRef}>
      <polyline
        points={lines.competenceBlue}
        strokeWidth="0.75"
        stroke={props.red ? 'var(--color-radar-compare)' : 'var(--color-radar-blue)'}
        fill={props.red ? 'var(--color-radar-compare-lighter)' : 'var(--color-radar-blue)'}
        opacity={props.red ? '0.3' : '1'}
      >
        {previousLines && (
          <animate
            attributeName="points"
            dur="250ms"
            begin="indefinite"
            fill="freeze"
            from={previousLines.competenceBlue}
            to={lines.competenceBlue}
          />
        )}
      </polyline>
      <polyline
        points={lines.competenceGreen}
        strokeWidth="0.75"
        stroke={props.red ? 'var(--color-radar-compare)' : 'var(--color-radar-green)'}
        fill={props.red ? 'var(--color-radar-compare-lighter)' : 'var(--color-radar-green)'}
        opacity={props.red ? '0.3' : '1'}
      >
        {previousLines && (
          <animate
            attributeName="points"
            dur="250ms"
            begin="indefinite"
            fill="freeze"
            from={previousLines.competenceGreen}
            to={lines.competenceGreen}
          />
        )}
      </polyline>
      <polyline
        points={lines.competenceYellow}
        strokeWidth="0.75"
        stroke={props.red ? 'var(--color-radar-compare)' : 'var(--color-radar-yellow)'}
        fill={props.red ? 'var(--color-radar-compare-lighter)' : 'var(--color-radar-yellow)'}
        opacity={props.red ? '0.3' : '1'}
      >
        {previousLines && (
          <animate
            attributeName="points"
            dur="250ms"
            begin="indefinite"
            fill="freeze"
            from={previousLines.competenceYellow}
            to={lines.competenceYellow}
          />
        )}
      </polyline>

      <polyline
        className="polyline-vector"
        points={lines.vector}
        strokeWidth="0.75"
        stroke={props.red ? 'var(--color-radar-compare)' : 'rgb(var(--bg))'}
        fill={props.red ? 'var(--color-radar-compare-lighter)' : 'rgb(var(--bg))'}
        opacity="0.4"
      >
        {previousLines && (
          <animate
            attributeName="points"
            dur="250ms"
            begin="indefinite"
            fill="freeze"
            from={previousLines.vector}
            to={lines.vector}
          />
        )}
      </polyline>

      <polyline
        points={lines.dimension}
        strokeWidth="0.75"
        stroke={props.red ? 'var(--color-radar-compare)' : 'rgb(var(--bg))'}
        fill={props.red ? 'var(--color-radar-compare-lighter)' : 'rgb(var(--bg))'}
        opacity={props.red ? '0.6' : '0.5'}
      >
        {previousLines && (
          <animate
            attributeName="points"
            dur="250ms"
            begin="indefinite"
            fill="freeze"
            from={previousLines.dimension}
            to={lines.dimension}
          />
        )}
      </polyline>

      {/* White edges. One path holding all 27 ring segments as subpaths — they were 27 elements with
          byte-identical attributes, and a stroked multi-subpath path paints exactly the same. */}
      {!props.red && <path fill="none" stroke="var(--color-card)" strokeMiterlimit="10" d={circleAll} />}
    </g>
  )
}

export const generateLineArray = (indicators: Radar['indicators'], compare?: boolean) => {
  const competences = [
    indicators.competences[0][compare ? 'valueToCompare' : 'value'] ?? indicators.competences[0]['value'],
    indicators.competences[1][compare ? 'valueToCompare' : 'value'] ?? indicators.competences[1]['value'],
    indicators.competences[2][compare ? 'valueToCompare' : 'value'] ?? indicators.competences[2]['value'],
    indicators.competences[3][compare ? 'valueToCompare' : 'value'] ?? indicators.competences[3]['value'],
    indicators.competences[4][compare ? 'valueToCompare' : 'value'] ?? indicators.competences[4]['value'],
    indicators.competences[5][compare ? 'valueToCompare' : 'value'] ?? indicators.competences[5]['value'],
    indicators.competences[6][compare ? 'valueToCompare' : 'value'] ?? indicators.competences[6]['value'],
    indicators.competences[7][compare ? 'valueToCompare' : 'value'] ?? indicators.competences[7]['value'],
    indicators.competences[8][compare ? 'valueToCompare' : 'value'] ?? indicators.competences[8]['value'],
    indicators.competences[9][compare ? 'valueToCompare' : 'value'] ?? indicators.competences[9]['value'],
    indicators.competences[10][compare ? 'valueToCompare' : 'value'] ?? indicators.competences[10]['value'],
    indicators.competences[11][compare ? 'valueToCompare' : 'value'] ?? indicators.competences[11]['value'],
    indicators.competences[12][compare ? 'valueToCompare' : 'value'] ?? indicators.competences[12]['value'],
    indicators.competences[13][compare ? 'valueToCompare' : 'value'] ?? indicators.competences[13]['value'],
    indicators.competences[14][compare ? 'valueToCompare' : 'value'] ?? indicators.competences[14]['value'],
    indicators.competences[15][compare ? 'valueToCompare' : 'value'] ?? indicators.competences[15]['value'],
    indicators.competences[16][compare ? 'valueToCompare' : 'value'] ?? indicators.competences[16]['value'],
    indicators.competences[17][compare ? 'valueToCompare' : 'value'] ?? indicators.competences[17]['value'],
  ] as const

  const vectors = [
    indicators.vectors[0][compare ? 'valueToCompare' : 'value'] ?? indicators.vectors[0]['value'],
    indicators.vectors[1][compare ? 'valueToCompare' : 'value'] ?? indicators.vectors[1]['value'],
    indicators.vectors[2][compare ? 'valueToCompare' : 'value'] ?? indicators.vectors[2]['value'],
    indicators.vectors[3][compare ? 'valueToCompare' : 'value'] ?? indicators.vectors[3]['value'],
    indicators.vectors[4][compare ? 'valueToCompare' : 'value'] ?? indicators.vectors[4]['value'],
    indicators.vectors[5][compare ? 'valueToCompare' : 'value'] ?? indicators.vectors[5]['value'],
  ] as const

  const dimensions = [
    indicators.dimensions[0][compare ? 'valueToCompare' : 'value'] ?? indicators.dimensions[0]['value'],
    indicators.dimensions[1][compare ? 'valueToCompare' : 'value'] ?? indicators.dimensions[1]['value'],
    indicators.dimensions[2][compare ? 'valueToCompare' : 'value'] ?? indicators.dimensions[2]['value'],
  ] as const

  const lines: {
    competenceBlue: [number, number][]
    competenceGreen: [number, number][]
    competenceYellow: [number, number][]
    vector: [number, number][]
    dimension: [number, number][]
  } = {
    competenceBlue: [[162, 162]],
    competenceGreen: [[162, 162]],
    competenceYellow: [[162, 162]],
    vector: [],
    dimension: [],
  }

  let corner: number
  let coord: { x: number; y: number } | undefined

  corner =
    competences[0] > competences[17]
      ? competences[0] - (competences[0] - competences[17])
      : competences[0] + (competences[17] - competences[0])
  coord = coordinatesMedium[corner]?.['blue-yellow']
  if (coord) lines.competenceBlue.push([coord.x, coord.y])

  corner =
    competences[7] > competences[6]
      ? competences[7] - (competences[7] - competences[6])
      : competences[7] + (competences[6] - competences[7])
  coord = coordinatesMedium[corner]?.['blue-green']
  if (coord) lines.competenceGreen.push([coord.x, coord.y])

  corner =
    competences[13] > competences[12]
      ? competences[13] - (competences[13] - competences[12])
      : competences[13] + (competences[12] - competences[13])
  coord = coordinatesMedium[corner]?.['green-yellow']
  if (coord) lines.competenceYellow.push([coord.x, coord.y])

  lines.competenceBlue.push([
    coordinates[0].competences[competences[0]].x,
    coordinates[0].competences[competences[0]].y,
  ])
  lines.competenceBlue.push([
    coordinates[1].competences[competences[1]].x,
    coordinates[1].competences[competences[1]].y,
  ])
  lines.competenceBlue.push([
    coordinates[2].competences[competences[2]].x,
    coordinates[2].competences[competences[2]].y,
  ])
  lines.competenceBlue.push([
    coordinates[3].competences[competences[3]].x,
    coordinates[3].competences[competences[3]].y,
  ])
  lines.competenceBlue.push([
    coordinates[4].competences[competences[4]].x,
    coordinates[4].competences[competences[4]].y,
  ])
  lines.competenceBlue.push([
    coordinates[5].competences[competences[5]].x,
    coordinates[5].competences[competences[5]].y,
  ])

  lines.competenceGreen.push([
    coordinates[6].competences[competences[6]].x,
    coordinates[6].competences[competences[6]].y,
  ])
  lines.competenceGreen.push([
    coordinates[7].competences[competences[7]].x,
    coordinates[7].competences[competences[7]].y,
  ])
  lines.competenceGreen.push([
    coordinates[8].competences[competences[8]].x,
    coordinates[8].competences[competences[8]].y,
  ])
  lines.competenceGreen.push([
    coordinates[9].competences[competences[9]].x,
    coordinates[9].competences[competences[9]].y,
  ])
  lines.competenceGreen.push([
    coordinates[10].competences[competences[10]].x,
    coordinates[10].competences[competences[10]].y,
  ])
  lines.competenceGreen.push([
    coordinates[11].competences[competences[11]].x,
    coordinates[11].competences[competences[11]].y,
  ])

  lines.competenceYellow.push([
    coordinates[12].competences[competences[12]].x,
    coordinates[12].competences[competences[12]].y,
  ])
  lines.competenceYellow.push([
    coordinates[13].competences[competences[13]].x,
    coordinates[13].competences[competences[13]].y,
  ])
  lines.competenceYellow.push([
    coordinates[14].competences[competences[14]].x,
    coordinates[14].competences[competences[14]].y,
  ])
  lines.competenceYellow.push([
    coordinates[15].competences[competences[15]].x,
    coordinates[15].competences[competences[15]].y,
  ])
  lines.competenceYellow.push([
    coordinates[16].competences[competences[16]].x,
    coordinates[16].competences[competences[16]].y,
  ])
  lines.competenceYellow.push([
    coordinates[17].competences[competences[17]].x,
    coordinates[17].competences[competences[17]].y,
  ])

  corner =
    competences[7] > competences[6]
      ? competences[7] - (competences[7] - competences[6])
      : competences[7] + (competences[6] - competences[7])
  coord = coordinatesMedium[corner]?.['blue-green']
  if (coord) lines.competenceBlue.push([coord.x, coord.y])

  corner =
    competences[13] > competences[12]
      ? competences[13] - (competences[13] - competences[12])
      : competences[13] + (competences[12] - competences[13])
  coord = coordinatesMedium[corner]?.['green-yellow']
  if (coord) lines.competenceGreen.push([coord.x, coord.y])

  corner =
    competences[0] > competences[17]
      ? competences[0] - (competences[0] - competences[17])
      : competences[0] + (competences[17] - competences[0])
  coord = coordinatesMedium[corner]?.['blue-yellow']
  if (coord) lines.competenceYellow.push([coord.x, coord.y])

  lines.vector.push([coordinates[0].vectors[vectors[0]].x, coordinates[0].vectors[vectors[0]].y])
  lines.vector.push([coordinates[1].vectors[vectors[1]].x, coordinates[1].vectors[vectors[1]].y])
  lines.vector.push([coordinates[2].vectors[vectors[2]].x, coordinates[2].vectors[vectors[2]].y])
  lines.vector.push([coordinates[3].vectors[vectors[3]].x, coordinates[3].vectors[vectors[3]].y])
  lines.vector.push([coordinates[4].vectors[vectors[4]].x, coordinates[4].vectors[vectors[4]].y])
  lines.vector.push([coordinates[5].vectors[vectors[5]].x, coordinates[5].vectors[vectors[5]].y])

  lines.vector.push([coordinates[0].vectors[vectors[0]].x, coordinates[0].vectors[vectors[0]].y])

  lines.dimension.push([coordinates[0].dimensions[dimensions[0]].x, coordinates[0].dimensions[dimensions[0]].y])
  lines.dimension.push([coordinates[1].dimensions[dimensions[1]].x, coordinates[1].dimensions[dimensions[1]].y])
  lines.dimension.push([coordinates[2].dimensions[dimensions[2]].x, coordinates[2].dimensions[dimensions[2]].y])

  lines.dimension.push([coordinates[0].dimensions[dimensions[0]].x, coordinates[0].dimensions[dimensions[0]].y])

  const linesText = {
    competenceBlue: '',
    competenceGreen: '',
    competenceYellow: '',
    vector: '',
    dimension: '',
  }

  lines.competenceBlue.forEach((elem) => {
    linesText.competenceBlue += `${elem.join()} `
  })
  lines.competenceGreen.forEach((elem) => {
    linesText.competenceGreen += `${elem.join()} `
  })
  lines.competenceYellow.forEach((elem) => {
    linesText.competenceYellow += `${elem.join()} `
  })
  lines.vector.forEach((elem) => {
    linesText.vector += `${elem.join()} `
  })
  lines.dimension.forEach((elem) => {
    linesText.dimension += `${elem.join()} `
  })

  return linesText
}
